import { normalizeUiLang, SUPPORTED_UI_LANGS, type UiLang } from "site/services/i18n";

export type RuleTitleSource = {
  titleRu?: string | null;
  titleEn?: string | null;
  titleFr?: string | null;
};

export type RuleContentSource = RuleTitleSource & {
  ru?: string | null;
  en?: string | null;
  fr?: string | null;
};

function ruleTitleForLang(source: RuleTitleSource | null | undefined, lang: UiLang): string {
  if (!source) {
    return "";
  }
  if (lang === "ru") {
    return String(source.titleRu ?? "").trim();
  }
  if (lang === "en") {
    return String(source.titleEn ?? "").trim();
  }
  return String(source.titleFr ?? "").trim();
}

function ruleMarkupForLang(source: RuleContentSource | null | undefined, lang: UiLang): string {
  if (!source) {
    return "";
  }
  if (lang === "ru") {
    return String(source.ru ?? "");
  }
  if (lang === "en") {
    return String(source.en ?? "");
  }
  return String(source.fr ?? "");
}

function pickWithFallback(
  source: RuleTitleSource | RuleContentSource | null | undefined,
  lang: UiLang,
  read: (source: RuleTitleSource | RuleContentSource, lang: UiLang) => string,
  fallbackLang: UiLang = "ru",
): string {
  if (!source) {
    return "";
  }
  const picked = read(source, lang);
  if (picked) {
    return picked;
  }
  if (lang !== fallbackLang) {
    const fallback = read(source, fallbackLang);
    if (fallback) {
      return fallback;
    }
  }
  for (const code of SUPPORTED_UI_LANGS) {
    const text = read(source, code);
    if (text) {
      return text;
    }
  }
  return "";
}

export function pickRuleTitle(
  source: RuleTitleSource | null | undefined,
  lang: string | null | undefined,
  fallbackLang: UiLang = "ru",
): string {
  const uiLang = normalizeUiLang(String(lang ?? fallbackLang));
  return pickWithFallback(source, uiLang, ruleTitleForLang, fallbackLang);
}

export function pickRuleMarkup(
  source: RuleContentSource | null | undefined,
  lang: string | null | undefined,
  fallbackLang: UiLang = "ru",
): string {
  const uiLang = normalizeUiLang(String(lang ?? fallbackLang));
  return pickWithFallback(source, uiLang, ruleMarkupForLang, fallbackLang);
}
